import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Trash2, Plus, Download, Share2, Copy, ChevronLeft, AlertTriangle, CheckCircle, Paperclip, Image as ImageIcon, Upload, X, FileText } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { key: 'vehicle', label: 'Vehicle', prefix: 'VEH', fields: [['vin','VIN'],['registration','Registration'],['engine','Engine no.'],['odometer','Odometer'],['licence_expiry','Licence expiry']] },
  { key: 'receivable', label: 'Receivable / Financial', prefix: 'FIN', fields: [['invoice_ref','Invoice / Agreement ref'],['debtor','Debtor'],['payment_schedule','Payment schedule']] },
  { key: 'plant', label: 'Plant & Machinery', prefix: 'PLT', fields: [['make','Make'],['model','Model'],['serial','Serial / VIN'],['year','Year']] },
  { key: 'industrial', label: 'Industrial equipment', prefix: 'IND', fields: [['hs_code','HS code'],['make','Make'],['model','Model']] },
  { key: 'property', label: 'Property', prefix: 'PROP', fields: [['title_deed','Title deed'],['erf','Erf / stand'],['market_value','Market value']] },
  { key: 'shares', label: 'Shares / receivables', prefix: 'SHR', fields: [['company_reg','Company reg.'],['shareholding','Shareholding %'],['spa_ref','SPA ref']] },
  { key: 'other', label: 'Other', prefix: 'SEC', fields: [] },
];

const zar = (v) => `R ${Number(v||0).toLocaleString('en-ZA', {maximumFractionDigits:0})}`;

const ACCEPT_MIME = [
  'image/jpeg','image/png','image/webp','image/gif','image/heic','image/heif',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/csv','text/plain'
].join(',');

function AttachmentsPanel({ item, uploading, onUpload, onOpen, onDelete }) {
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);
  const docs = item.documents || [];
  const pics = item.photos || [];

  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDrag(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length) onUpload(files);
  };
  const handlePick = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) onUpload(files);
    e.target.value = '';
  };
  const fmtSize = (n) => n < 1024 ? `${n} B` : n < 1_048_576 ? `${(n/1024).toFixed(1)} KB` : `${(n/1_048_576).toFixed(1)} MB`;

  return (
    <div className="space-y-3" data-testid={`attachments-panel-${item.id}`}>
      <div
        onDragOver={(e)=>{e.preventDefault(); setDrag(true);}}
        onDragLeave={()=>setDrag(false)}
        onDrop={handleDrop}
        onClick={()=>inputRef.current?.click()}
        className={`border-2 border-dashed rounded-md p-4 text-center cursor-pointer transition-colors ${drag ? 'border-emerald-500 bg-emerald-50' : 'border-slate-300 bg-white hover:bg-slate-50'}`}
        data-testid={`dropzone-${item.id}`}
      >
        <Upload className="h-5 w-5 inline mr-2 text-slate-500" />
        <span className="text-sm text-slate-600">
          {uploading ? 'Uploading…' : (<><strong>Drop files</strong> or click to browse — images, PDFs, Office docs (max 25 MB each)</>)}
        </span>
        <input ref={inputRef} type="file" multiple hidden accept={ACCEPT_MIME} onChange={handlePick} data-testid={`file-input-${item.id}`} />
      </div>

      {(pics.length > 0) && (
        <div>
          <div className="text-xs text-[#6C757D] mb-2 flex items-center gap-1"><ImageIcon className="h-3 w-3" /> Photos ({pics.length})</div>
          <div className="flex flex-wrap gap-2">
            {pics.map(p => (
              <div key={p.id} className="relative group border rounded-md overflow-hidden bg-white" style={{width: 96, height: 96}}>
                <button onClick={()=>onOpen(p.id)} className="w-full h-full flex items-center justify-center text-xs text-slate-500 hover:bg-slate-50" title={p.name}>
                  <ImageIcon className="h-6 w-6" />
                  <span className="sr-only">{p.name}</span>
                </button>
                <button onClick={()=>onDelete(p.id)} className="absolute top-1 right-1 bg-white/90 hover:bg-red-100 rounded p-0.5 opacity-0 group-hover:opacity-100 transition" data-testid={`del-photo-${p.id}`}>
                  <X className="h-3 w-3 text-red-600" />
                </button>
                <div className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[10px] px-1 py-0.5 truncate">{p.name}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {(docs.length > 0) && (
        <div>
          <div className="text-xs text-[#6C757D] mb-2 flex items-center gap-1"><FileText className="h-3 w-3" /> Documents ({docs.length})</div>
          <div className="space-y-1">
            {docs.map(d => (
              <div key={d.id} className="flex items-center justify-between bg-white border rounded px-3 py-2 text-sm">
                <button onClick={()=>onOpen(d.id)} className="flex items-center gap-2 text-emerald-700 hover:underline truncate mr-2" title={d.name}>
                  <FileText className="h-4 w-4 shrink-0" />
                  <span className="truncate">{d.name}</span>
                </button>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-xs text-[#6C757D]">{fmtSize(d.size || 0)}</span>
                  <button onClick={()=>onDelete(d.id)} className="text-red-600 hover:text-red-700" data-testid={`del-doc-${d.id}`}><X className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function UseOfFundsEditor({ value, onChange }) {
  const rows = value || [];
  const update = (i, patch) => onChange(rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const add = () => onChange([...rows, { heading: '', items: [] }]);
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-3">
      {rows.length === 0 && <div className="text-xs text-[#6C757D] italic">No headings yet. Add one below.</div>}
      {rows.map((r, i) => (
        <div key={i} className="border rounded-md p-3 bg-slate-50 space-y-2">
          <div className="flex gap-2">
            <Input value={r.heading || ''} onChange={e => update(i, { heading: e.target.value })} placeholder="Heading (e.g. Structuring, legal & regulatory)" className="flex-1" />
            <Button size="sm" variant="ghost" onClick={() => remove(i)}><X className="h-4 w-4 text-red-600" /></Button>
          </div>
          <Textarea rows={3} value={(r.items || []).join('\n')} onChange={e => update(i, { items: e.target.value.split('\n').map(x => x.trim()).filter(Boolean) })}
            placeholder="Bullets — one per line" />
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={add} type="button"><Plus className="h-3 w-3 mr-1" />Add heading</Button>
    </div>
  );
}

export default function DealSecuritySchedulePage() {
  const { dealId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  const [proposal, setProposal] = useState({ borrower:'', facility_amount:0, interest_rate_pct:0, term_months:0, pledge_cover_pct:110, min_cover_pct:125, executive_summary:'', lender_bullets:[], use_of_funds:[], opportunity_narrative:'', notes:'', sources:[] });
  const [totals, setTotals] = useState({});
  const [dealMeta, setDealMeta] = useState({});
  const [items, setItems] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [shares, setShares] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [importing, setImporting] = useState(false);
  const csvInputRef = useRef(null);

  const [newItem, setNewItem] = useState({
    category:'vehicle', name:'', description:'', owner:'',
    quantity:'', condition:'', location:'',
    value_actual:0, discount_pct:0, details:{}
  });
  const [genForm, setGenForm] = useState({ start_date: new Date().toISOString().slice(0,10), monthly_amount: 0, total_amount: 0, bulk_final: true });
  const [shareForm, setShareForm] = useState({ access_mode:'auth', pin:'', expires_in_days: 30 });
  const [expandedItemId, setExpandedItemId] = useState(null);
  const [uploadingFor, setUploadingFor] = useState(null);   // itemId currently uploading

  const load = useCallback(async () => {
    try {
      const [p, it, sc, sh] = await Promise.all([
        axios.get(`${API_URL}/api/admin/deals/${dealId}/proposal`, { headers }),
        axios.get(`${API_URL}/api/admin/deals/${dealId}/security-items`, { headers }),
        axios.get(`${API_URL}/api/admin/deals/${dealId}/schedules`, { headers }),
        axios.get(`${API_URL}/api/admin/deals/${dealId}/shares`, { headers }),
      ]);
      if (p.data.proposal && Object.keys(p.data.proposal).length) setProposal(prev => ({...prev, ...p.data.proposal}));
      if (p.data.deal) setDealMeta(p.data.deal);
      setTotals(p.data.totals || {});
      setItems(it.data.items || []);
      setSchedules(sc.data.schedules || []);
      setShares(sh.data.shares || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Load failed'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, token]);

  useEffect(() => { load(); }, [load]);

  const saveProposal = async () => {
    try {
      const r = await axios.put(`${API_URL}/api/admin/deals/${dealId}/proposal`, proposal, { headers });
      setTotals(r.data.totals || {});
      toast.success('Proposal saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const addItem = async () => {
    if (!newItem.name) { toast.error('Asset name required'); return; }
    try {
      const r = await axios.post(`${API_URL}/api/admin/deals/${dealId}/security-items`, newItem, { headers });
      setItems([...items, r.data.item]);
      setTotals(r.data.totals);
      setNewItem({ category:'vehicle', name:'', description:'', owner:'', quantity:'', condition:'', location:'', value_actual:0, discount_pct:0, details:{} });
      toast.success('Security item added');
    } catch (e) { toast.error(e.response?.data?.detail || 'Add failed'); }
  };

  const importCsv = async (file, replace) => {
    if (!file) return;
    if (replace && !window.confirm('This will delete ALL existing security items on this deal and replace them with the CSV. Continue?')) return;
    setImporting(true);
    try {
      const form = new FormData(); form.append('file', file);
      const r = await axios.post(
        `${API_URL}/api/admin/deals/${dealId}/security-items/import-csv?replace=${replace ? 'true' : 'false'}`,
        form,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } },
      );
      toast.success(r.data.message);
      setTotals(r.data.totals || {});
      // Refresh list
      const it = await axios.get(`${API_URL}/api/admin/deals/${dealId}/security-items`, { headers });
      setItems(it.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'CSV import failed');
    } finally {
      setImporting(false);
    }
  };

  const exportCsv = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/deals/${dealId}/security-items/export.csv`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = `deal-${dealId}-assets.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('CSV export failed'); }
  };

  const delItem = async (id) => {
    if (!window.confirm('Delete this security item?')) return;
    try {
      const r = await axios.delete(`${API_URL}/api/admin/deals/${dealId}/security-items/${id}`, { headers });
      setItems(items.filter(i => i.id !== id));
      setTotals(r.data.totals);
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const generateSchedule = async () => {
    try {
      const r = await axios.post(`${API_URL}/api/admin/deals/${dealId}/schedules/generate`, genForm, { headers });
      const save = await axios.post(`${API_URL}/api/admin/deals/${dealId}/schedules`, { name: `Schedule ${schedules.length+1}`, rows: r.data.rows }, { headers });
      setSchedules([...schedules, save.data.schedule]);
      toast.success(`Schedule generated (${r.data.rows.length} instalments)`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Generate failed'); }
  };

  const delSchedule = async (id) => {
    if (!window.confirm('Delete this schedule?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/deals/${dealId}/schedules/${id}`, { headers });
      setSchedules(schedules.filter(s => s.id !== id));
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const createShare = async () => {
    if (shareForm.access_mode === 'pin' && !shareForm.pin) { toast.error('PIN required'); return; }
    try {
      const r = await axios.post(`${API_URL}/api/admin/deals/${dealId}/share`, shareForm, { headers });
      const url = window.location.origin + r.data.share_url;
      await navigator.clipboard.writeText(url).catch(()=>{});
      toast.success(`Share link created (${r.data.access_mode}) — copied to clipboard`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Share failed'); }
  };

  const exportPdf = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/deals/${dealId}/export-pdf`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = `deal-${dealId}-security-schedule.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('PDF export failed'); }
  };

  const uploadAttachments = async (itemId, fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploadingFor(itemId);
    let success = 0;
    for (const file of fileList) {
      const form = new FormData();
      form.append('file', file);
      try {
        const r = await axios.post(
          `${API_URL}/api/admin/deals/${dealId}/security-items/${itemId}/attachments`,
          form,
          { headers: { ...headers, 'Content-Type': 'multipart/form-data' } },
        );
        // Merge new attachment into the item in state
        setItems(prev => prev.map(it => {
          if (it.id !== itemId) return it;
          const arr = r.data.kind === 'photo' ? 'photos' : 'documents';
          return { ...it, [arr]: [ ...(it[arr] || []), r.data.attachment ] };
        }));
        success += 1;
      } catch (e) {
        toast.error(e.response?.data?.detail || `Upload failed: ${file.name}`);
      }
    }
    setUploadingFor(null);
    if (success) toast.success(`${success} file${success>1?'s':''} uploaded`);
  };

  const openAttachment = async (itemId, attachmentId) => {
    try {
      const r = await axios.get(
        `${API_URL}/api/admin/deals/${dealId}/security-items/${itemId}/attachments/${attachmentId}/url`,
        { headers },
      );
      window.open(r.data.url, '_blank', 'noopener');
    } catch (e) { toast.error(e.response?.data?.detail || 'Cannot open file'); }
  };

  const deleteAttachment = async (itemId, attachmentId) => {
    if (!window.confirm('Remove this attachment?')) return;
    try {
      const r = await axios.delete(
        `${API_URL}/api/admin/deals/${dealId}/security-items/${itemId}/attachments/${attachmentId}`,
        { headers },
      );
      const arr = r.data.kind === 'photo' ? 'photos' : 'documents';
      setItems(prev => prev.map(it => it.id !== itemId ? it :
        { ...it, [arr]: (it[arr] || []).filter(a => a.id !== attachmentId) }));
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const cat = CATEGORIES.find(c => c.key === newItem.category) || CATEGORIES[0];
  const meets = totals.meets_minimum;

  return (
    <div className="space-y-6" data-testid="deal-security-page">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <Button variant="ghost" onClick={()=>navigate('/admin/deals')} className="mb-2" data-testid="back-to-deals">
            <ChevronLeft className="h-4 w-4 mr-1" /> Deals
          </Button>
          <h1 className="text-3xl font-bold text-[#212529] font-['Manrope']">
            {dealMeta.deal_name || 'Loan Proposal & Security Schedule'}
          </h1>
          <p className="text-[#6C757D] mt-1 text-sm">
            {dealMeta.sector && <Badge variant="outline" className="mr-2">{dealMeta.sector}</Badge>}
            {dealMeta.risk_rating && <Badge variant="outline" className="mr-2">Risk: {dealMeta.risk_rating}</Badge>}
            {proposal.borrower && <span>Borrower: <strong>{proposal.borrower}</strong> · </span>}
            <code className="text-xs">{dealId}</code>
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportPdf} data-testid="export-pdf-btn"><Download className="h-4 w-4 mr-2" /> Export PDF</Button>
        </div>
      </div>

      {/* Cover-ratio summary */}
      {totals.principal > 0 && (
        <Card className={`border-0 shadow-sm ${meets ? 'bg-emerald-50' : 'bg-red-50'}`}>
          <CardContent className="p-5 grid grid-cols-2 md:grid-cols-6 gap-4">
            <div><p className="text-xs text-[#6C757D]">Principal</p><p className="font-bold text-lg">{zar(totals.principal)}</p></div>
            <div><p className="text-xs text-[#6C757D]">Pledged @ {totals.pledge_cover_pct}%</p><p className="font-bold text-lg">{zar(totals.pledged)}</p></div>
            <div><p className="text-xs text-[#6C757D]">Actual pool</p><p className="font-bold text-lg">{zar(totals.total_actual)}</p></div>
            <div><p className="text-xs text-[#6C757D]">Discounted pool</p><p className="font-bold text-lg">{zar(totals.total_discounted)}</p></div>
            <div><p className="text-xs text-[#6C757D]">Actual cover</p><p className="font-bold text-lg">{totals.cover_actual_pct}%</p></div>
            <div><p className="text-xs text-[#6C757D]">Discounted cover</p><p className={`font-bold text-lg ${meets ? 'text-emerald-700' : 'text-red-700'}`}>
              {totals.cover_discounted_pct}% {meets ? <CheckCircle className="inline h-4 w-4 mb-1" /> : <AlertTriangle className="inline h-4 w-4 mb-1" />}
            </p></div>
            {!meets && (
              <div className="col-span-full text-sm text-red-700">
                Below {totals.min_cover_pct}% minimum — shortfall of <strong>{zar(totals.shortfall)}</strong> in discounted security.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="proposal" className="space-y-4">
        <TabsList>
          <TabsTrigger value="proposal">Proposal</TabsTrigger>
          <TabsTrigger value="items">Security items ({items.length})</TabsTrigger>
          <TabsTrigger value="schedule">Repayment ({schedules.length})</TabsTrigger>
          <TabsTrigger value="share">Share ({shares.filter(s=>!s.revoked).length})</TabsTrigger>
        </TabsList>

        {/* Proposal */}
        <TabsContent value="proposal">
          <Card><CardHeader><CardTitle>Facility Terms</CardTitle><CardDescription>Auto-populated from the parent deal. Edit freely — this is what the borrower &amp; investor see.</CardDescription></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><label className="text-xs text-[#6C757D]">Borrower</label><Input value={proposal.borrower||''} onChange={e=>setProposal({...proposal, borrower:e.target.value})} data-testid="proposal-borrower" /></div>
              <div><label className="text-xs text-[#6C757D]">Borrower — full name / ID / reg no.</label><Input value={proposal.borrower_full_name||''} onChange={e=>setProposal({...proposal, borrower_full_name:e.target.value})} placeholder="Tyron Bos (ID 870511 5184 083)" /></div>
              <div><label className="text-xs text-[#6C757D]">Lender</label><Input value={proposal.lender||'Plane Tree Capital (PTC)'} onChange={e=>setProposal({...proposal, lender:e.target.value})} /></div>
              <div><label className="text-xs text-[#6C757D]">Facility amount (ZAR)</label><Input type="number" value={proposal.facility_amount||0} onChange={e=>setProposal({...proposal, facility_amount:parseFloat(e.target.value)||0})} /></div>
              <div><label className="text-xs text-[#6C757D]">Interest rate (% p.a.)</label><Input type="number" step="0.01" value={proposal.interest_rate_pct||0} onChange={e=>setProposal({...proposal, interest_rate_pct:parseFloat(e.target.value)||0})} /></div>
              <div><label className="text-xs text-[#6C757D]">Term (months)</label><Input type="number" value={proposal.term_months||0} onChange={e=>setProposal({...proposal, term_months:parseInt(e.target.value)||0})} /></div>
              <div><label className="text-xs text-[#6C757D]">Pledge cover %</label><Input type="number" value={proposal.pledge_cover_pct||110} onChange={e=>setProposal({...proposal, pledge_cover_pct:parseFloat(e.target.value)||110})} /></div>
              <div><label className="text-xs text-[#6C757D]">Min cover % (warning threshold)</label><Input type="number" value={proposal.min_cover_pct||125} onChange={e=>setProposal({...proposal, min_cover_pct:parseFloat(e.target.value)||125})} /></div>
              <div className="md:col-span-2"><label className="text-xs text-[#6C757D]">Executive summary</label><Textarea rows={5} value={proposal.executive_summary||''} onChange={e=>setProposal({...proposal, executive_summary:e.target.value})} /></div>

              <div className="md:col-span-2">
                <label className="text-xs text-[#6C757D]">&ldquo;What the lender receives&rdquo; — one bullet per line</label>
                <Textarea rows={4} value={(proposal.lender_bullets||[]).join('\n')}
                  onChange={e=>setProposal({...proposal, lender_bullets: e.target.value.split('\n').map(x=>x.trim()).filter(Boolean)})}
                  placeholder={"30% p.a. — up to R720 000 over 12 months\nFully secured — 110% cover of principal\nFirst right to fund the next project phase"} />
              </div>

              <div className="md:col-span-2">
                <label className="text-xs text-[#6C757D]">Use of Loan Funds — one heading per row, bullets inside</label>
                <UseOfFundsEditor value={proposal.use_of_funds||[]} onChange={(v)=>setProposal({...proposal, use_of_funds: v})} />
              </div>

              <div className="md:col-span-2">
                <label className="text-xs text-[#6C757D]">The Opportunity — narrative (plain text or markdown)</label>
                <Textarea rows={5} value={proposal.opportunity_narrative||''} onChange={e=>setProposal({...proposal, opportunity_narrative:e.target.value})}
                  placeholder="Copper resource, near-term milestones, next phase, portfolio…" />
              </div>

              <div className="md:col-span-2">
                <label className="text-xs text-[#6C757D]">Notes &amp; Sources — free text</label>
                <Textarea rows={4} value={proposal.notes||''} onChange={e=>setProposal({...proposal, notes:e.target.value})} />
              </div>

              <div className="md:col-span-2"><Button onClick={saveProposal} data-testid="save-proposal-btn">Save proposal</Button></div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Items */}
        <TabsContent value="items">
          <Card><CardHeader className="flex flex-row items-center justify-between">
            <div><CardTitle>Add security item</CardTitle><CardDescription>Category-specific fields render below based on the chosen type.</CardDescription></div>
            <div className="flex gap-2">
              <input ref={csvInputRef} type="file" accept=".csv,text/csv" hidden
                     onChange={(e) => { const f = e.target.files?.[0]; if (f) importCsv(f, items.length > 0); e.target.value=''; }}
                     data-testid="csv-import-input" />
              <Button variant="outline" size="sm" onClick={() => csvInputRef.current?.click()} disabled={importing} data-testid="csv-import-btn">
                <Upload className="h-4 w-4 mr-1" /> {importing ? 'Importing…' : 'Import CSV'}
              </Button>
              <Button variant="outline" size="sm" onClick={exportCsv} data-testid="csv-export-btn" disabled={items.length === 0}>
                <Download className="h-4 w-4 mr-1" /> Export CSV
              </Button>
            </div>
          </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div><label className="text-xs text-[#6C757D]">Category</label>
                <select className="w-full h-10 px-3 rounded-md border bg-background text-sm" value={newItem.category} onChange={e=>setNewItem({...newItem, category:e.target.value, details:{}})}>
                  {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
              </div>
              <div className="md:col-span-2"><label className="text-xs text-[#6C757D]">Asset name</label><Input value={newItem.name} onChange={e=>setNewItem({...newItem, name:e.target.value})} /></div>
              <div><label className="text-xs text-[#6C757D]">Owner</label><Input value={newItem.owner} onChange={e=>setNewItem({...newItem, owner:e.target.value})} /></div>
              <div className="md:col-span-4"><label className="text-xs text-[#6C757D]">Description</label><Textarea rows={2} value={newItem.description} onChange={e=>setNewItem({...newItem, description:e.target.value})} /></div>
              <div><label className="text-xs text-[#6C757D]">Qty</label><Input value={newItem.quantity} onChange={e=>setNewItem({...newItem, quantity:e.target.value})} placeholder="1 / 40 shares" /></div>
              <div><label className="text-xs text-[#6C757D]">Condition</label><Input value={newItem.condition} onChange={e=>setNewItem({...newItem, condition:e.target.value})} placeholder="New / Used" /></div>
              <div className="md:col-span-2"><label className="text-xs text-[#6C757D]">Location</label><Input value={newItem.location} onChange={e=>setNewItem({...newItem, location:e.target.value})} placeholder="Where the asset is held" /></div>
              <div><label className="text-xs text-[#6C757D]">Value (ZAR)</label><Input type="number" value={newItem.value_actual} onChange={e=>setNewItem({...newItem, value_actual:parseFloat(e.target.value)||0})} /></div>
              <div><label className="text-xs text-[#6C757D]">Discount %</label><Input type="number" value={newItem.discount_pct} onChange={e=>setNewItem({...newItem, discount_pct:parseFloat(e.target.value)||0})} /></div>
              {cat.fields.map(([key,label]) => (
                <div key={key}><label className="text-xs text-[#6C757D]">{label}</label>
                  <Input value={newItem.details[key]||''} onChange={e=>setNewItem({...newItem, details:{...newItem.details, [key]:e.target.value}})} />
                </div>
              ))}
              <div className="md:col-span-4"><Button onClick={addItem} data-testid="add-item-btn"><Plus className="h-4 w-4 mr-2" /> Add item</Button></div>
            </CardContent>
          </Card>

          {/* Category filter chips */}
          {items.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid="category-filter">
              <button onClick={() => setCategoryFilter('all')}
                      className={`px-3 py-1 rounded-full text-xs font-medium border ${categoryFilter === 'all' ? 'bg-[#1D2D2A] text-white border-[#1D2D2A]' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}>
                All ({items.length})
              </button>
              {[...new Set(items.map(i => i.category))].map(c => {
                const count = items.filter(i => i.category === c).length;
                const label = (CATEGORIES.find(cc => cc.key === c) || {}).label || c;
                return (
                  <button key={c} onClick={() => setCategoryFilter(c)}
                          data-testid={`category-chip-${c}`}
                          className={`px-3 py-1 rounded-full text-xs font-medium border ${categoryFilter === c ? 'bg-[#1D2D2A] text-white border-[#1D2D2A]' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}>
                    {label} ({count})
                  </button>
                );
              })}
            </div>
          )}

          <Card className="mt-4"><CardContent className="p-0"><Table>
            <TableHeader><TableRow>
              <TableHead>Ref</TableHead><TableHead>Category</TableHead><TableHead>Asset</TableHead>
              <TableHead>Qty</TableHead><TableHead>Condition</TableHead><TableHead>Location</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead className="text-right">Actual</TableHead><TableHead className="text-right">Disc %</TableHead><TableHead className="text-right">Discounted</TableHead>
              <TableHead>Files</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow><TableCell colSpan={12} className="text-center text-[#6C757D] py-12">No security items yet. Add one above or Import CSV.</TableCell></TableRow>
              ) : items.filter(i => categoryFilter === 'all' || i.category === categoryFilter).flatMap(i => {
                const docs = i.documents || [];
                const pics = i.photos || [];
                const attachCount = docs.length + pics.length;
                const expanded = expandedItemId === i.id;
                const rows = [(
                <TableRow key={i.id}>
                  <TableCell className="font-mono text-xs">{i.ref_code}</TableCell>
                  <TableCell><Badge variant="outline">{i.category}</Badge></TableCell>
                  <TableCell>
                    <div className="font-medium">{i.name}</div>
                    {i.description && <div className="text-xs text-[#6C757D] max-w-md">{i.description.slice(0,140)}{i.description.length > 140 && '…'}</div>}
                    {i.details && Object.entries(i.details).filter(([_,v])=>v).slice(0,3).map(([k,v]) => (
                      <div key={k} className="text-xs text-[#6C757D]">{k}: <span className="font-mono">{v}</span></div>
                    ))}
                  </TableCell>
                  <TableCell className="text-xs">{i.quantity || '—'}</TableCell>
                  <TableCell className="text-xs">{i.condition || '—'}</TableCell>
                  <TableCell className="text-xs">{i.location || '—'}</TableCell>
                  <TableCell className="text-sm">{i.owner||'—'}</TableCell>
                  <TableCell className="text-right">{zar(i.value_actual)}</TableCell>
                  <TableCell className="text-right">{i.discount_pct}%</TableCell>
                  <TableCell className="text-right font-semibold">{zar(i.value_discounted)}</TableCell>
                  <TableCell>
                    <Button size="sm" variant={expanded ? "default" : "outline"} className="h-7 gap-1"
                      onClick={() => setExpandedItemId(expanded ? null : i.id)}
                      data-testid={`toggle-attachments-${i.id}`}
                      title="Documents & photos">
                      <Paperclip className="h-3 w-3" />
                      <span className="text-xs">{attachCount}</span>
                    </Button>
                  </TableCell>
                  <TableCell><Button size="sm" variant="ghost" onClick={()=>delItem(i.id)}><Trash2 className="h-4 w-4 text-red-600" /></Button></TableCell>
                </TableRow>
                )];
                if (expanded) rows.push(
                  <TableRow key={`${i.id}-attach`} className="bg-slate-50">
                    <TableCell colSpan={12} className="p-4">
                      <AttachmentsPanel
                        item={i}
                        uploading={uploadingFor === i.id}
                        onUpload={(files) => uploadAttachments(i.id, files)}
                        onOpen={(aid) => openAttachment(i.id, aid)}
                        onDelete={(aid) => deleteAttachment(i.id, aid)}
                      />
                    </TableCell>
                  </TableRow>
                );
                return rows;
              })}
            </TableBody>
          </Table></CardContent></Card>
        </TabsContent>

        {/* Schedule */}
        <TabsContent value="schedule">
          <Card><CardHeader><CardTitle>Generate a schedule</CardTitle><CardDescription>Creates a new scenario you can then edit inline.</CardDescription></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <div><label className="text-xs text-[#6C757D]">Start date</label><Input type="date" value={genForm.start_date} onChange={e=>setGenForm({...genForm, start_date:e.target.value})} /></div>
              <div><label className="text-xs text-[#6C757D]">Monthly amount</label><Input type="number" value={genForm.monthly_amount} onChange={e=>setGenForm({...genForm, monthly_amount:parseFloat(e.target.value)||0})} /></div>
              <div><label className="text-xs text-[#6C757D]">Total to pay</label><Input type="number" value={genForm.total_amount} onChange={e=>setGenForm({...genForm, total_amount:parseFloat(e.target.value)||0})} /></div>
              <div className="flex items-end"><label className="text-xs text-[#6C757D] flex items-center gap-2"><input type="checkbox" checked={genForm.bulk_final} onChange={e=>setGenForm({...genForm, bulk_final:e.target.checked})} /> Bulk final</label></div>
              <div className="flex items-end"><Button onClick={generateSchedule}>Generate</Button></div>
            </CardContent>
          </Card>

          {schedules.map(s => (
            <Card key={s.id} className="mt-4"><CardHeader className="flex flex-row items-center justify-between">
              <div><CardTitle>{s.name}</CardTitle>{s.subtitle && <CardDescription>{s.subtitle}</CardDescription>}</div>
              <Button size="sm" variant="ghost" onClick={()=>delSchedule(s.id)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
            </CardHeader>
            <CardContent className="p-0"><Table>
              <TableHeader><TableRow><TableHead>When</TableHead><TableHead>Description</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
              <TableBody>
                {(s.rows||[]).map((r,idx) => (
                  <TableRow key={idx}><TableCell className="font-mono text-xs">{r.when}</TableCell><TableCell>{r.description}</TableCell><TableCell className="text-right font-semibold">{zar(r.amount)}</TableCell></TableRow>
                ))}
                <TableRow className="bg-slate-50"><TableCell colSpan={2} className="font-bold">Total</TableCell><TableCell className="text-right font-bold">{zar((s.rows||[]).reduce((a,r)=>a+(r.amount||0),0))}</TableCell></TableRow>
              </TableBody>
            </Table></CardContent></Card>
          ))}
        </TabsContent>

        {/* Share */}
        <TabsContent value="share">
          <Card><CardHeader><CardTitle>Create share link</CardTitle><CardDescription>Read-only public view of this deal. Pick who can see it.</CardDescription></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div><label className="text-xs text-[#6C757D]">Access mode</label>
                <select className="w-full h-10 px-3 rounded-md border bg-background text-sm" value={shareForm.access_mode} onChange={e=>setShareForm({...shareForm, access_mode:e.target.value})}>
                  <option value="open">Open (anyone with link)</option>
                  <option value="pin">PIN-protected</option>
                  <option value="auth">Requires portal login</option>
                </select>
              </div>
              {shareForm.access_mode==='pin' && <div><label className="text-xs text-[#6C757D]">PIN</label><Input value={shareForm.pin} onChange={e=>setShareForm({...shareForm, pin:e.target.value})} /></div>}
              <div><label className="text-xs text-[#6C757D]">Expires in (days)</label><Input type="number" value={shareForm.expires_in_days} onChange={e=>setShareForm({...shareForm, expires_in_days:parseInt(e.target.value)||30})} /></div>
              <div className="flex items-end"><Button onClick={createShare} data-testid="create-share-btn"><Share2 className="h-4 w-4 mr-2" /> Create link</Button></div>
            </CardContent>
          </Card>

          <Card className="mt-4"><CardContent className="p-0"><Table>
            <TableHeader><TableRow><TableHead>Mode</TableHead><TableHead>URL</TableHead><TableHead>Expires</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {shares.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center text-[#6C757D] py-8">No shares yet.</TableCell></TableRow>
              : shares.map(s => {
                const url = `${window.location.origin}/deal/${s.token}`;
                return (
                <TableRow key={s.id}>
                  <TableCell><Badge>{s.access_mode}</Badge></TableCell>
                  <TableCell><code className="text-xs break-all">{url}</code></TableCell>
                  <TableCell className="text-xs">{s.expires_at?.slice(0,10)}</TableCell>
                  <TableCell>
                    {s.revoked ? <Badge variant="outline" className="text-red-700 border-red-300">Revoked</Badge>
                    : <Button size="sm" variant="ghost" onClick={()=>navigator.clipboard.writeText(url).then(()=>toast.success('Copied'))}><Copy className="h-4 w-4" /></Button>}
                  </TableCell>
                </TableRow>);
              })}
            </TableBody>
          </Table></CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
